/**
 * Measuring tool «Punkt til bakke»: the vertical height from a point in a point cloud down to the
 * terrain.
 *
 * One click per measurement. While the pointer moves, a DOM tooltip (`pointToGroundHoverStore`,
 * rendered by `PointToGroundHoverTooltip.svelte`) previews «Høyde over bakke: 3,42 m» and says
 * whether the pointer is outside the point cloud. A left click locks the measurement as a vertical
 * polyline from the point down to the terrain, a point on the ground and a label. Right click or Esc
 * ends the tool.
 *
 * The preview is a DOM element and not a Cesium label, so it never ends up in the depth buffer that
 * `scene.pickPosition` reads, and nothing that removes drawings can remove it.
 *
 * Template: `toggleDrawDistanceLineOnMap` (polylineTool.ts) for input and datasource, and
 * `adjustHeightTool.ts` for module-level state. The toggle is called twice per session (on/off), so
 * the state cannot live in closure-local variables.
 *
 * The entities use the measure line's id prefixes (`distance_polyline:`, `distance_polyline_point:`,
 * `distance_polyline_label:`) and `EntityTypes.DISTANCE_MEASURE_LINE_PART`, so selection
 * suppression, «Slett tegninger» and `removeMeasureDistanceLine` work without changes elsewhere.
 *
 * Heights: both `scene.pickPosition` and `sampleTerrainMostDetailed` give ellipsoidal heights
 * (WGS84), so the difference does not depend on a datum.
 */
import { get, writable } from 'svelte/store';
import {
	ArcType,
	Cartesian2,
	Cartesian3,
	Cartographic,
	Cesium3DTileset,
	Color,
	ConstantProperty,
	defined,
	Entity,
	EntityCollection,
	HorizontalOrigin,
	NearFarScalar,
	PointGraphics,
	PolylineGraphics,
	PropertyBag,
	sampleTerrainMostDetailed,
	Scene,
	ScreenSpaceEventType,
	VerticalOrigin
} from 'cesium';
import { v4 as uuidv4 } from 'uuid';
import { useCesiumViewer } from '../cesium-viewer';
import { useDataSources } from '../cesium-datasources';
import {
	disableDefaultLeftClickHandler,
	removeAllScreenSpaceInputActions,
	toggleEscapeToolListener
} from '../map-tools';
import { mapToolsStore } from '../map-tools-store';
import { EntityTypes } from '../map-utils';
import { toast } from '$lib/components/organisms/toast/toast';

/** Marker in the PropertyBag, so the cascade removal recognises this tool's entities. */
const TOOL_TAG = 'pointToGround';
const DISABLE_DEPTH_TEST_DISTANCE = 1.2742018e7;
const LOADING_TEXT = 'Terreng lastes…';

const ID_PREFIXES = ['distance_polyline:', 'distance_polyline_point:', 'distance_polyline_label:'];

export type PointToGroundHover = {
	/** Pointer position in viewport coordinates (already includes the canvas offset). */
	x: number;
	y: number;
	text: string;
	outsidePointCloud: boolean;
};

/** The hover preview; `null` hides the tooltip. */
export const pointToGroundHoverStore = writable<PointToGroundHover | null>(null);

const state: {
	/** The latest pointer position, processed once per animation frame. */
	pendingHoverPosition: Cartesian2 | undefined;
	hoverFrame: number | undefined;
	/** Locks LEFT_CLICK while `sampleTerrainMostDetailed` waits for terrain tiles. */
	busy: boolean;
	/** Once per viewer: the cascade removal when one of the three siblings is removed. */
	cascadeListenerAttached: boolean;
} = {
	pendingHoverPosition: undefined,
	hoverFrame: undefined,
	busy: false,
	cascadeListenerAttached: false
};

/** «Høyde over bakke: 3,42 m». Negative when the point is below the terrain. */
function heightOverGroundText(topHeightM: number, groundHeightM: number): string {
	const fixed = (topHeightM - groundHeightM).toFixed(2);
	const normalized = Number(fixed) === 0 ? '0.00' : fixed;
	return `Høyde over bakke: ${normalized.replace('.', ',')} m`;
}

/**
 * World position under the pointer. `scene.pickPosition` reads the depth buffer and therefore hits
 * the point cloud itself (and buildings and 3D models); `globe.pick` is the fallback when depth
 * picking is not supported or hits nothing.
 */
function pickWorldPosition(scene: Scene, windowPosition: Cartesian2): Cartesian3 | undefined {
	if (scene.pickPositionSupported) {
		const picked = scene.pickPosition(windowPosition);
		if (defined(picked)) return picked;
	}
	const ray = scene.camera.getPickRay(windowPosition);
	if (!ray) return undefined;
	const onGlobe = scene.globe.pick(ray, scene);
	return defined(onGlobe) ? onGlobe : undefined;
}

/** True when the object under the pointer belongs to a 3D tileset (a point cloud). */
function pickHitsTileset(scene: Scene, windowPosition: Cartesian2): boolean {
	const picked = scene.pick(windowPosition);
	return !!picked && picked.primitive instanceof Cesium3DTileset;
}

function propertyBag(lineId: string) {
	return new PropertyBag({
		entityType: EntityTypes.DISTANCE_MEASURE_LINE_PART,
		lineId,
		tool: TOOL_TAG
	});
}

function labelGraphics(text: string) {
	return {
		show: true,
		showBackground: true,
		text,
		font: '15px Poppins',
		eyeOffset: new Cartesian3(0.0, 0.0, -10.0),
		horizontalOrigin: HorizontalOrigin.CENTER,
		verticalOrigin: VerticalOrigin.BOTTOM,
		pixelOffset: new Cartesian2(0.0, -20.0),
		pixelOffsetScaleByDistance: new NearFarScalar(1.5e2, 3.0, 1.5e7, 0.5),
		disableDepthTestDistance: DISABLE_DEPTH_TEST_DISTANCE
	};
}

/** Hides the tooltip and drops any hover update that is waiting for the next frame. */
function hideHover() {
	if (state.hoverFrame !== undefined) cancelAnimationFrame(state.hoverFrame);
	state.hoverFrame = undefined;
	state.pendingHoverPosition = undefined;
	pointToGroundHoverStore.set(null);
}

function processHover() {
	state.hoverFrame = undefined;
	const windowPosition = state.pendingHoverPosition;
	state.pendingHoverPosition = undefined;
	if (!windowPosition) return;

	const viewer = useCesiumViewer();
	const { scene } = viewer;
	const position = pickWorldPosition(scene, windowPosition);
	const carto = position ? Cartographic.fromCartesian(position) : undefined;
	if (!carto) {
		pointToGroundHoverStore.set(null);
		return;
	}
	const groundHeightM = scene.globe.getHeight(carto);
	const rect = viewer.canvas.getBoundingClientRect();
	pointToGroundHoverStore.set({
		x: rect.left + windowPosition.x,
		y: rect.top + windowPosition.y,
		text:
			groundHeightM === undefined ? LOADING_TEXT : heightOverGroundText(carto.height, groundHeightM),
		outsidePointCloud: !pickHitsTileset(scene, windowPosition)
	});
}

/**
 * «Slett tegninger» (`removeDrawnEntity`) removes only label + line or point + label, never all
 * three. This listener removes the remaining siblings with the same `lineId` when one of them goes.
 */
function attachCascadeRemoval(collection: EntityCollection) {
	if (state.cascadeListenerAttached) return;
	state.cascadeListenerAttached = true;
	collection.collectionChanged.addEventListener(
		(_collection: EntityCollection, _added: Entity[], removed: Entity[]) => {
			const lineIds = new Set<string>();
			for (const entity of removed) {
				const values = entity.properties?.getValue();
				if (values?.tool === TOOL_TAG && typeof values.lineId === 'string') {
					lineIds.add(values.lineId);
				}
			}
			if (lineIds.size === 0) return;
			const siblings = collection.values.filter((entity) => {
				const lineId = entity.properties?.getValue()?.lineId;
				return (
					typeof lineId === 'string' &&
					lineIds.has(lineId) &&
					ID_PREFIXES.some((prefix) => entity.id.startsWith(prefix))
				);
			});
			for (const sibling of siblings) collection.remove(sibling);
		}
	);
}

function addLockedMeasurement(top: Cartesian3, topCarto: Cartographic, groundHeightM: number) {
	const { measureDistancePolylineDataSource } = useDataSources();
	const lineId = uuidv4();
	const ground = Cartesian3.fromRadians(topCarto.longitude, topCarto.latitude, groundHeightM);

	measureDistancePolylineDataSource.entities.add({
		id: `distance_polyline:${lineId}`,
		properties: propertyBag(lineId),
		polyline: new PolylineGraphics({
			positions: new ConstantProperty([top, ground]),
			width: 3,
			arcType: ArcType.NONE,
			material: Color.YELLOW,
			clampToGround: false
		})
	});
	measureDistancePolylineDataSource.entities.add({
		id: `distance_polyline_point:${lineId}`,
		position: ground,
		properties: propertyBag(lineId),
		point: new PointGraphics({
			color: Color.WHITE,
			outlineColor: Color.BLACK,
			outlineWidth: 1,
			pixelSize: 6,
			disableDepthTestDistance: DISABLE_DEPTH_TEST_DISTANCE
		})
	});
	measureDistancePolylineDataSource.entities.add({
		id: `distance_polyline_label:${lineId}`,
		position: top,
		properties: propertyBag(lineId),
		label: labelGraphics(heightOverGroundText(topCarto.height, groundHeightM))
	});
}

/** Keeps only the latest pointer position; `processHover` picks it up on the next frame. */
function onMouseMove(event: { endPosition: Cartesian2 }) {
	state.pendingHoverPosition = Cartesian2.clone(event.endPosition);
	if (state.hoverFrame === undefined) {
		state.hoverFrame = requestAnimationFrame(processHover);
	}
}

async function onLeftClick(event: { position: Cartesian2 }) {
	if (state.busy) return;
	const viewer = useCesiumViewer();
	const { scene } = viewer;
	viewer.selectedEntity = undefined;

	const top = pickWorldPosition(scene, event.position);
	const topCarto = top ? Cartographic.fromCartesian(top) : undefined;
	if (!top || !topCarto) return;

	state.busy = true;
	try {
		let groundHeightM: number | undefined;
		try {
			const [sampled] = await sampleTerrainMostDetailed(viewer.terrainProvider, [
				new Cartographic(topCarto.longitude, topCarto.latitude)
			]);
			groundHeightM = sampled?.height;
		} catch (error) {
			// A terrain provider without tile availability (e.g. the ellipsoid): use the loaded globe height.
			console.warn('[pointToGround] sampleTerrainMostDetailed failed, using globe.getHeight', error);
			groundHeightM = scene.globe.getHeight(topCarto);
		}
		// The tool may have been turned off (Esc, side menu) while waiting for the terrain.
		if (!get(mapToolsStore).pointToGround.toolEnabled) return;
		if (groundHeightM === undefined || !Number.isFinite(groundHeightM)) {
			toast({
				type: 'warning',
				title: 'Terrenghøyde ikke tilgjengelig',
				description: 'Terrenget under punktet er ikke lastet ennå – prøv igjen om et øyeblikk.'
			});
			return;
		}
		addLockedMeasurement(top, topCarto, groundHeightM);
	} finally {
		state.busy = false;
	}
}

/**
 * Hides the tooltip and removes the Esc and canvas `mouseleave` listeners without touching
 * `mapToolsStore`. Idempotent. Called from `disableAllTools`/`disableAllToolsExceptToggleToolByName`
 * when another tool or a side menu turns this tool off, and from the off branch of
 * `togglePointToGroundTool`.
 */
export function cleanupPointToGroundTool(): void {
	hideHover();
	useCesiumViewer().canvas.removeEventListener('mouseleave', hideHover);
	toggleEscapeToolListener({ toggleOn: false });
	state.busy = false;
}

/**
 * Turns the tool on or off. Registered as `deactivateTool` in `mapToolsStore.pointToGround`, so Esc
 * (`handleKeyPress`) and right click land here.
 *
 * A function declaration on purpose: map-tools-store.ts references it at module init
 * (`initialMapToolStore`), and the two modules import each other. Function declarations are
 * hoisted during ESM instantiation, so the order is harmless.
 */
export function togglePointToGroundTool(): void {
	const viewer = useCesiumViewer();
	const { screenSpaceEventHandler } = viewer;
	const { measureDistancePolylineDataSource } = useDataSources();

	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

	if (!viewer.dataSources.contains(measureDistancePolylineDataSource)) {
		viewer.dataSources.add(measureDistancePolylineDataSource);
	}
	attachCascadeRemoval(measureDistancePolylineDataSource.entities);

	mapToolsStore.disableAllToolsExceptToggleToolByName('pointToGround', viewer);
	viewer.selectedEntity = undefined;

	if (get(mapToolsStore).pointToGround.toolEnabled) {
		toggleEscapeToolListener({ toggleOn: true });
		disableDefaultLeftClickHandler(screenSpaceEventHandler);
		state.busy = false;
		// Cesium sends no MOUSE_MOVE outside the canvas, so the tooltip would stay where the pointer left.
		viewer.canvas.addEventListener('mouseleave', hideHover);

		screenSpaceEventHandler.setInputAction(onMouseMove, ScreenSpaceEventType.MOUSE_MOVE);
		screenSpaceEventHandler.setInputAction(onLeftClick, ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.setInputAction(togglePointToGroundTool, ScreenSpaceEventType.RIGHT_CLICK);
	} else {
		cleanupPointToGroundTool();
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
		// Restores Cesium's original LEFT_CLICK (entity selection).
		removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	}
}
